import { useEffect, useState } from 'react'
import { Coffee, Star } from 'lucide-react'
import { fetchFood, type LifeFoodEntry } from '../lib/api'
import LifeModal from '../components/LifeModal'

function Stars({ rating }: { rating?: number }) {
  if (!rating) return null
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={12}
          className={i < rating ? 'text-amber-300 fill-amber-300' : 'text-amber-300/20'}
        />
      ))}
    </div>
  )
}

export default function FoodSection() {
  const [items, setItems] = useState<LifeFoodEntry[]>([])
  const [active, setActive] = useState<LifeFoodEntry | null>(null)

  useEffect(() => {
    fetchFood().then(setItems)
  }, [])

  return (
    <section id="food" className="scroll-mt-32 py-16">
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-4xl font-heading font-semibold amber-gradient-text">Food</h2>
          <p className="text-amber-100/70 mt-1 max-w-prose">
            Dishes worth remembering. Mostly Indian, increasingly everything.
          </p>
        </div>
        <Coffee className="text-amber-300/60" size={28} />
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((f, i) => (
          <button
            key={f.id}
            onClick={() => setActive(f)}
            className={`rounded-xl p-6 text-left ${i === 0 ? 'life-card-warm sm:col-span-2 lg:col-span-2' : 'life-card'}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-amber-200/70">
                {f.cuisine}
                {f.city ? ` · ${f.city}` : ''}
              </span>
              <Stars rating={f.rating} />
            </div>
            <h3 className={`font-heading font-medium text-amber-50 mb-2 ${i === 0 ? 'text-3xl' : 'text-2xl'}`}>{f.title}</h3>
            <p className={`text-amber-100/70 leading-relaxed ${i === 0 ? 'text-base line-clamp-4' : 'text-sm line-clamp-3'}`}>{f.notes}</p>
          </button>
        ))}
      </div>

      <LifeModal
        isOpen={active !== null}
        onClose={() => setActive(null)}
        title={active?.title ?? ''}
        size="md"
      >
        {active && (
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-amber-200/70">
                {active.cuisine}
                {active.city ? ` · ${active.city}` : ''}
              </span>
              <Stars rating={active.rating} />
            </div>
            <p className="text-amber-100/85 leading-relaxed whitespace-pre-line">{active.notes}</p>
          </div>
        )}
      </LifeModal>
    </section>
  )
}
